import type React from 'react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, api, apiUpload, useAuth } from '@shop/api-client'
import type { Branch, Category, Draft, OpeningSession, Product, Warehouse } from '../api/types'
import { Perm } from '../api/types'
import { toNumber } from '../ui/format'
import { ImageField } from '../ui/ImageField'
import {
  Banner, Button, Empty, ErrorBanner, Field, NumberInput, Panel, Select, Spinner, TextInput,
} from '@shop/ui'

/**
 * الجردُ السريع — <b>الباركودُ أوّلًا، ثمّ يقرّر النظامُ ما هذا</b>.
 *
 * <p>
 * ⚠ <b>والفعلان مختلفان جذريًّا وإن بدا الحقلُ واحدًا:</b>
 * </p>
 * <ul>
 * <li><b>باركودٌ معروف</b> ⇒ الصنفُ قائم، والمكتوبُ هو <b>الكمّيّةُ المعدودةُ في
 *   الرفّ</b>. فيُقارَن بالرصيد ويُكتب <b>الفرقُ</b> مستندَ جردٍ — ولا يُنشأ
 *   صنفٌ ثانٍ باسمٍ مشابه.</li>
 * <li><b>باركودٌ مجهول</b> ⇒ صنفٌ جديد، فتُفتح بقيّةُ الحقول ويُعرَّف،
 *   والباركودُ يُحفظ معه.</li>
 * </ul>
 *
 * <p>
 * ⚠ <b>وهذا الترتيبُ هو الذي يمنع الازدواج.</b> شاشةٌ تبدأ بالاسم تجعل من
 * يجرد رفًّا فيه صنفٌ أُدخل سابقًا يُنشئه من جديد — <b>فيصير للصنف الواحد
 * صنفان ورصيدان وتقريران</b>، ولا يُكتشف إلا بعد شهورٍ حين لا يُطابَق شيء.
 * </p>
 *
 * <p>
 * ⚠ <b>والفرقُ يُكتب مستندًا ولا يُستبدل الرصيد.</b> كتابةُ رقمٍ جديدٍ فوق
 * القديم تمحو أنّ هناك فرقًا أصلًا — <b>وفرقُ الجرد هو الرقمُ الذي يُبحث
 * عنه</b>: منه يُعرف التلفُ والسرقةُ وخطأُ الإدخال.
 * </p>
 */
export default function Stocktake() {
  const { session, can } = useAuth()
  const queries = useQueryClient()

  const [barcode, setBarcode] = useState('')
  const [found, setFound] = useState<Match | null>(null)
  const [looked, setLooked] = useState(false)

  const [categoryId, setCategoryId] = useState('')
  const [nameAr, setNameAr] = useState('')
  const [qty, setQty] = useState('')
  const [cost, setCost] = useState('')
  const [price, setPrice] = useState('')
  const [image, setImage] = useState<File | null>(null)

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [done, setDone] = useState<Row[]>([])

  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: () => api<Branch[]>('/api/v1/branches'),
  })

  const branchId = session?.me.activeBranchId ?? branches.data?.[0]?.id ?? ''

  const warehouses = useQuery({
    queryKey: ['warehouses', branchId],
    queryFn: () => api<Warehouse[]>(`/api/v1/warehouses?branchId=${branchId}`),
    enabled: branchId !== '' && can(Perm.warehouseView),
  })

  const categories = useQuery({
    queryKey: ['categories'],
    queryFn: () => api<Category[]>('/api/v1/catalog/categories'),
    staleTime: 5 * 60_000,
  })

  const sessions = useQuery({
    queryKey: ['opening'],
    queryFn: () => api<OpeningSession[]>('/api/v1/inventory/opening'),
  })

  // ⚠ **والمستودعُ لا يُسأل عنه حين يكون واحدًا فعّالًا** — والمعطَّلُ لا يعود
  //   في القائمة، فتعطيلُ ما لا يُستعمل يجعل هذا السطرَ يختار وحدَه.
  const usable = (warehouses.data ?? []).filter((w) => w.isActive && w.kind !== 'TRANSIT')
  const [chosenWarehouse, setChosenWarehouse] = useState<string | null>(null)
  const warehouseId = chosenWarehouse ?? usable[0]?.id ?? ''

  const openSession = (sessions.data ?? []).find(
    (s) => s.warehouseId === warehouseId && s.status === 'OPEN',
  )

  const counted = toNumber(qty)

  /**
   * ⚠ <b>والمادّةُ الواحدةُ تكون في أكثر من مكان.</b>
   *
   * <p>
   * من يجرد المحلَّ أوّلَ مرّةٍ يجد الصنفَ على الرفّ، ثمّ يجد منه في الدرج،
   * ثمّ في المخزن. و«المعدودُ في الرفّ» يعني <b>المجموعَ كلَّه</b> — فمن وجد
   * خمسًا في المكان الثاني وكتب «٥» والدفترُ فيه ٤، يُسجَّل <b>فرقٌ ‎+١</b>
   * لا ‎+٥. <b>والخطأُ لا يظهر</b>: الرقمُ معقولٌ والمستندُ يُرحَّل.
   * </p>
   *
   * <p>
   * فوضعان صريحان، ولا حسابَ ذهنيٌّ على من يعدّ.
   * </p>
   */
  const [mode, setMode] = useState<'total' | 'add'>('total')

  /**
   * ⚠ **والاستلامُ غيرُ الجرد** — قرارُ المالك ٢٠٢٦-٠٩-٢٨: الموظّفُ يستلم
   *   البضاعةَ بلا كلفة، وتنزل الكمّيّةُ فورًا فتُباع، والكلفةُ تأتي بفاتورة.
   *
   *   الجردُ **يعدّ** ما على الرفّ ويكتب الفرق، ويُقيّم الزيادةَ بالمتوسّط.
   *   والاستلامُ **يُضيف** ما وصل بكلفةٍ مجهولة. ولو خُلطا لَقُيّمت البضاعةُ
   *   المشتراةُ بالمتوسّط ثمّ أضافت الفاتورةُ قيمتَها فوقه — **فتتضاعف**.
   */
  const [purpose, setPurpose] = useState<'count' | 'receive'>('count')
  const receiving = purpose === 'receive'

  const difference = found && counted !== null
    ? (receiving || mode === 'add' ? round(counted) : round(counted - found.onHand))
    : 0

  /** ما سيصير عليه الدفترُ بعد التسجيل — يُقال قبل الضغط لا بعده. */
  const after = found ? round(found.onHand + difference) : 0

  // ⚠ **وما سُجّل في هذه الجلسة للصنف نفسِه.**
  //
  //   من يجرد رفًّا يمسح الصنفَ مرّتين بلا قصد — والرصيدُ في الدفتر يكون قد
  //   تحرّك بالمسحة الأولى، فيرى رقمًا صحيحًا ولا يدري أنّه هو الذي كتبه.
  //   **فيعدّه ثانيةً ويُثبّت الخطأ.** والسطرُ هنا يُظهر تاريخَه معه.
  const earlier = found
    ? done.filter((row) => row.variantId === found.variantId)
    : []

  const resetRow = () => {
    setBarcode('')
    setFound(null)
    setLooked(false)
    setNameAr('')
    setQty('')
    setCost('')
    setPrice('')
    setImage(null)
    setError(null)

    // والوضعُ يعود إلى «المجموع»: البحثُ التالي يقرّره من تاريخ الجلسة.
    setMode('total')

    focusBarcode()
  }

  /**
   * ⚠ **والبحثُ أوّلُ فعلٍ في السطر** — قبل أن يُكتب اسمٌ أو سعر.
   *
   *   و404 ليست خطأً هنا: باركودٌ مجهولٌ هو الحالةُ الثانيةُ من حالتين لا
   *   إخفاق. **ورسالةٌ حمراءُ على صنفٍ جديدٍ تُربك من يجرد** وتجعله يتوقّف
   *   عند كلّ صنفٍ لم يُدخَل بعد.
   */
  const lookup = async () => {
    const code = barcode.trim()

    if (code === '' || busy) {
      return
    }

    setBusy(true)
    setError(null)
    setFound(null)

    // ⚠ **ولا بحثَ قبل أن يُعرف المستودع.** الماسحُ يُطلق فورَ فتح الصفحة،
    //   وقائمةُ المستودعات ما تزال في الطريق — فكان نداءُ الرصيد يفشل،
    //   ويبتلعه `catch`، **فتقول الشاشةُ «لا صنفَ بهذا الباركود» عن صنفٍ
    //   قائم**. ثمّ يردّ الخادمُ عند الحفظ «هذا الرقمُ لصنفٍ قائم» — فيقرأ
    //   الموظّفُ جوابين متناقضين ويُعيد إدخالَ ما هو مُدخَل.
    if (warehouseId === '') {
      setError(new Error('المستودعُ لم يُحمَّل بعد — أعد المسح بعد لحظة.'))
      setBusy(false)
      return
    }

    let variant: { id: string; productId: string }

    try {
      variant = await api<{ id: string; productId: string }>(
        `/api/v1/products/by-barcode/${encodeURIComponent(code)}`)
    } catch (failure) {
      // **و404 وحدَها تعني «صنفٌ جديد».** أيُّ إخفاقٍ آخر — منعٌ أو انقطاعٌ
      // أو عطبٌ — يُقال كما هو، ولا يُترجَم إلى دعوةٍ لإنشاء صنفٍ مكرَّر.
      if (isNotFound(failure)) {
        setFound(null)
      } else {
        setError(failure)
        setBusy(false)
        return
      }

      setLooked(true)
      setBusy(false)
      return
    }

    try {
      const product = await api<Product>(`/api/v1/products/${variant.productId}`)

      let onHand: number | null = null

      // ⚠ **والرصيدُ ثانويٌّ هنا**: إن تعذّرت قراءتُه فالصنفُ قائمٌ على أيّ
      //   حال، وإخفاؤه خلف «لا صنفَ بهذا الباركود» هو الكذبةُ نفسُها.
      try {
        const stock = await api<{ qtyOnHand: number }>(
          `/api/v1/inventory/on-hand?variantId=${variant.id}&warehouseId=${warehouseId}`)
        onHand = stock.qtyOnHand
      } catch (failure) {
        setError(failure)
      }

      setFound({
        productId: variant.productId,
        variantId: variant.id,
        nameAr: product.nameAr,
        categoryName: product.categoryName,
        onHand: onHand ?? 0,
        onHandKnown: onHand !== null,
      })

      // ⚠ **والمسحةُ الثانيةُ للصنف نفسِه تعني «وجدتُه في مكانٍ آخر».**
      //
      //   فالوضعُ يتبدّل وحدَه إلى «أضف»، والحقلُ يبدأ فارغًا لا من الدفتر:
      //   رقمٌ جاهزٌ في حقلِ إضافةٍ يُضاعف الرصيدَ بضغطةٍ واحدة.
      const again = done.some((row) => row.variantId === variant.id)

      setMode(again ? 'add' : 'total')

      // وفي وضع «المجموع» يبدأ المعدودُ من الدفتر: من يجرد يصحّح الفرقَ لا
      // يكتب الرقمَ كلَّه من جديد.
      setQty(again || onHand === null ? '' : String(onHand))
    } catch (failure) {
      setError(failure)
    } finally {
      setLooked(true)
      setBusy(false)
    }
  }

  /** صنفٌ قائم: يُكتب الفرقُ مستندَ جرد. */
  const adjust = async () => {
    if (!found || counted === null || difference === 0) {
      return
    }

    setBusy(true)
    setError(null)

    try {
      // ⚠ **والردُّ يحمل `id` في أعلاه، بلا `header`.**
      //
      //   كان هنا `{ header: { id } }` — فيُرمى `TypeError` بعد أن يكون
      //   المستندُ قد أُنشئ فعلًا على الخادم (٢٠١)، ويُعرض «تعذّر الاتّصالُ
      //   بالخادم» فيظنّ الموظّفُ أنّ شيئًا لم يقع. **وكان يقع نصفُه:**
      //   مستندُ تسويةٍ مفتوحٌ بلا سطورٍ ولا ترحيل، يتراكم مع كلّ محاولة.
      //
      //   ولا يُخمَّن شكلُ ردٍّ: هذا مقيسٌ على الخادم لا مقروءٌ من الذاكرة.
      const created = await api<{ id: string }>('/api/v1/adjustments', {
        method: 'POST',
        body: {
          warehouseId,
          // ⚠ **والسببُ يتبع اتّجاهَ الفرق** — لا سببٌ واحدٌ للزيادة والنقص:
          //   تقريرُ الفروق يُقرأ بالسبب، وسببٌ واحدٌ يجعله بلا معنى.
          reasonCode: receiving
            ? 'PURCHASE_PENDING'
            : difference > 0 ? 'COUNT_SURPLUS' : 'COUNT_SHORTAGE',

          // ⚠ **والسببُ يذكر أيَّ وضعٍ كُتب به** — من يقرأ المستندَ بعد شهرٍ
          //   يحتاج أن يعرف هل «٥» مجموعٌ أم إضافةٌ من مكانٍ ثانٍ.
          note: receiving
            ? `استلامُ بضاعةٍ لِـ«${found.nameAr}»: ${counted} قطعة — بانتظار فاتورة المورّد`
            : mode === 'add'
            ? `جردُ افتتاحٍ لِـ«${found.nameAr}»: وُجدت ${counted} في مكانٍ إضافيّ`
              + ` — الدفترُ ${found.onHand} ⇐ ${after}`
            : `فرقُ جردٍ لِـ«${found.nameAr}»: المعدودُ ${counted} والدفترُ ${found.onHand}`,
        },
      })

      await api(`/api/v1/adjustments/${created.id}/lines`, {
        method: 'PUT',
        body: {
          lines: [{
            variantId: found.variantId,
            qty: Math.abs(difference),
            direction: difference > 0 ? 1 : -1,
          }],
        },
      })

      await api(`/api/v1/adjustments/${created.id}/post`, { method: 'POST', body: {} })

      setDone((rows) => [
        {
          key: `${found.variantId}-${Date.now()}`,
          id: found.productId,
          nameAr: found.nameAr,
          detail: receiving
            ? `استُلمت ${counted}: ${found.onHand} ⇐ ${after} · بانتظار فاتورة`
            : mode === 'add'
            ? `أُضيفت ${counted} من مكانٍ آخر: ${found.onHand} ⇐ ${after}`
            : `جرد: ${found.onHand} ⇐ ${counted}`,
          qty: difference,
          variantId: found.variantId,
          countedQty: counted,
        },
        ...rows,
      ])

      resetRow()
      void queries.invalidateQueries({ queryKey: ['adjustments'] })
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  /** صنفٌ جديد: مسوّدةٌ تُفتح وتُملأ وتُعتمد — <b>بلا نقطةٍ جديدةٍ في الخادم</b>. */
  const create = async () => {
    setBusy(true)
    setError(null)

    try {
      const draft = await api<Draft>('/api/v1/intake/drafts', {
        method: 'POST',
        body: { source: 'MANUAL', branchId, warehouseId, rawText: nameAr.trim() },
      })

      if (image) {
        await apiUpload(`/api/v1/intake/drafts/${draft.id}/image`, image)
      }

      await api<Draft>(`/api/v1/intake/drafts/${draft.id}`, {
        method: 'PATCH',
        body: {
          nameAr: nameAr.trim(),
          barcode: barcode.trim() === '' ? null : barcode.trim(),
          qty: receiving ? null : counted,
          costPrice: receiving ? null : toNumber(cost),
          sellPrice: toNumber(price),
          warehouseId,
        },
      })

      const product = await api<Product>(
        `/api/v1/intake/drafts/${draft.id}/confirm`,
        { method: 'POST', body: { categoryId } },
      )

      // ⚠ **والكمّيّةُ في الاستلام تُدخَل بعد الإنشاء لا معه** — بسبب
      //   `PURCHASE_PENDING` وبكلفةٍ مجهولة. فلا يعتمد الاستلامُ على فترة
      //   الافتتاح، ويعمل قبل إقفالها وبعده.
      if (receiving && counted !== null && counted > 0) {
        // ⚠ **والاعتمادُ يعيد المسوّدةَ (`DraftView`) لا الصنف** — و`variantId`
        //   في أعلاها. خُمّن أوّلًا `variants[0].id` فكانت الشاشةُ سترمي «بلا
        //   متغيّر» عند كلّ صنفٍ جديدٍ يُستلم. والشكلُ مقروءٌ من العقد.
        const variantId = (product as unknown as { variantId: string | null }).variantId

        if (!variantId) {
          throw new Error('أُنشئ الصنفُ لكن بلا متغيّرٍ يُستلم عليه — أبلغ المالك.')
        }

        const received = await api<{ id: string }>('/api/v1/adjustments', {
          method: 'POST',
          body: {
            warehouseId,
            reasonCode: 'PURCHASE_PENDING',
            note: `استلامُ صنفٍ جديد «${nameAr.trim()}»: ${counted} قطعة — بانتظار فاتورة المورّد`,
          },
        })

        await api(`/api/v1/adjustments/${received.id}/lines`, {
          method: 'PUT',
          body: { lines: [{ variantId, qty: counted, direction: 1 }] },
        })

        await api(`/api/v1/adjustments/${received.id}/post`, { method: 'POST', body: {} })
      }

      setDone((rows) => [
        {
          key: product.id,
          id: product.id,
          nameAr: nameAr.trim(),
          detail: `صنفٌ جديد · ${product.categoryName ?? ''}`,
          qty: counted ?? 0,
        },
        ...rows,
      ])

      // ⚠ **والقسمُ وحدَه يبقى** — من يجرد رفَّ الكفرات يُدخل عشرين كفرًا.
      const keptCategory = categoryId

      resetRow()
      setCategoryId(keptCategory)

      void queries.invalidateQueries({ queryKey: ['products'] })
      void queries.invalidateQueries({ queryKey: ['categories'] })
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  const readyNew =
    looked && !found
    && nameAr.trim().length > 0 && categoryId !== ''
    && warehouseId !== '' && branchId !== ''
    && (counted ?? 0) > 0 && !busy

  // ⚠ **وفرقُ الجرد مستندُ تسوية** — فيطلب صلاحيّتَها. ولو تُرك الزرُّ ظاهرًا
  //   لَاكتشف الموظّفُ المنعَ بعد أن عدَّ الرفَّ كلَّه.
  const mayAdjust = can(Perm.adjustmentPost)

  // ⚠ **ولا يُرحَّل فرقٌ حُسب على رصيدٍ مجهول.** إن تعذّرت قراءةُ الدفتر فالفرقُ
  //   مقيسٌ على صفرٍ مُفترَض، وترحيلُه يكتب في المخزون رقمًا من عندنا.
  const readyCount =
    found !== null && found.onHandKnown
    && counted !== null && difference !== 0 && mayAdjust && !busy

  const onBarcodeKey = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      void lookup()
    }
  }

  const net = done.reduce((sum, row) => sum + row.qty, 0)

  return (
    <div className="space-y-4">
      <Panel title={receiving ? 'استلامُ بضاعة' : 'الجردُ السريع'}>
        {/* ⚠ **والغرضُ يُختار أوّلًا وبوضوح** — لأنّ الخطأ فيه يُفسد الكلفة:
            الجردُ يُقيّم الزيادةَ بالمتوسّط، والاستلامُ يُدخلها بكلفةٍ مجهولة
            تُنزلها الفاتورة. */}
        <div className="mb-4 grid grid-cols-2 gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => setPurpose('receive')}
            className={`rounded-xl border-2 p-3 text-right ${
              receiving ? 'border-brand bg-brand/10' : 'border-line'
            }`}
          >
            <div className="font-semibold">استلامُ بضاعة</div>
            <div className="text-xs text-ink-soft">وصلت من مورّد — تُباع فورًا، والكلفةُ بالفاتورة</div>
          </button>

          <button
            type="button"
            disabled={busy}
            onClick={() => setPurpose('count')}
            className={`rounded-xl border-2 p-3 text-right ${
              !receiving ? 'border-brand bg-brand/10' : 'border-line'
            }`}
          >
            <div className="font-semibold">جرد</div>
            <div className="text-xs text-ink-soft">عدُّ ما على الرفّ وتصحيحُ الفرق</div>
          </button>
        </div>

        {receiving && (
          <Banner tone="info">
            امسح الصنفَ واكتب الكمّيّةَ التي وصلت وسعرَ البيع. <b>الكمّيّةُ تنزل فورًا
            وتُباع</b>، والكلفةُ تُنزلها فاتورةُ المشتريات لاحقًا.
          </Banner>
        )}

        {sessions.isPending && <Spinner />}

        {/* ⚠ **والاستلامُ لا يحتاج فترةَ افتتاح** — يُنشئ الصنفَ بلا كمّيّةٍ ثمّ
            يُدخلها بسبب الاستلام. فالتحذيرُ للجرد وحدَه. */}
        {!receiving && sessions.data && !openSession && (
          <Banner tone="warn">
            لا فترةَ افتتاحٍ مفتوحةٌ لهذا المستودع — <b>ولن يُقبل صنفٌ جديد</b>.
            {' '}
            <Link className="underline" to="/opening">افتح الفترةَ من هنا</Link>
            {' '}(أو أعِد فتحَها إن كنتَ أغلقتَها).
            {' '}وتسويةُ كمّيّةِ صنفٍ قائمٍ تعمل على أيّ حال.
          </Banner>
        )}

        {usable.length > 1 && (
          <div className="mb-3">
            <Field label="المستودع">
              <Select value={warehouseId} onChange={setChosenWarehouse} disabled={busy}>
                {usable.map((warehouse) => (
                  <option key={warehouse.id} value={warehouse.id}>{warehouse.nameAr}</option>
                ))}
              </Select>
            </Field>
          </div>
        )}

        {usable.length === 1 && (
          <p className="mb-3 text-xs text-ink-soft">
            المستودع: <b>{usable[0]!.nameAr}</b> — الوحيدُ الفعّال، فلا يُسأل عنه.
          </p>
        )}

        {/* ① الباركودُ أوّلًا — وهو الذي يقرّر أيَّ نموذجٍ يُفتح. */}
        <div className="grid gap-3 sm:grid-cols-[2fr_auto]">
          <Field label="الباركود" hint="امسحه بالماسح — أو اكتبه واضغط Enter">
            <TextInput
              value={barcode}
              onChange={(value) => { setBarcode(value); setLooked(false); setFound(null) }}
              dir="ltr"
              name="stocktake-barcode"
              placeholder="6221234567890"
              disabled={busy}
              autoFocus
              onKeyDown={onBarcodeKey}
            />
          </Field>

          <div className="flex items-end">
            <Button disabled={barcode.trim() === '' || busy} onClick={() => void lookup()}>
              {busy ? 'يُبحث…' : 'ابحث'}
            </Button>
          </div>
        </div>

        {/* ②-أ صنفٌ قائم: الكمّيّةُ المعدودةُ والفرق. */}
        {found && (
          <div className="mt-4 rounded-md border border-line bg-surface p-3">
            <Banner tone="ok">
              صنفٌ قائم: <b>{found.nameAr}</b>
              {found.categoryName && ` — ${found.categoryName}`}.
              {' '}<b>وهذا تعديلُ كمّيّةٍ لا صنفٌ جديد.</b>
            </Banner>

            {/*
              ⚠ **وتاريخُ هذا الصنف في هذه الجلسة** — المسحةُ الثانيةُ تحتاج أن
                تعرف الأولى. والرصيدُ في الدفتر يكون قد تحرّك بها، فيبدو صحيحًا
                وهو من صنعِ من يقرؤه.
            */}
            {earlier.length > 0 && (
              <div className="mt-2">
                <Banner tone="warn">
                  <b>
                    عددتَ هذا الصنفَ في هذه الجلسة{' '}
                    {earlier.length === 1 ? 'مرّةً' : `${earlier.length} مرّات`}
                    {' '}— مجموعُ ما سجّلتَه{' '}
                    <span className="tabular" dir="ltr">
                      {earlier.reduce((sum, row) => round(sum + (row.countedQty ?? 0)), 0)}
                    </span>.
                  </b>
                  {' '}<b>والرصيدُ أدناه يشمله</b>، فالوضعُ انتقل إلى «أضِف ما وجدتَه هنا»:
                  اكتب ما في هذا المكان وحدَه.
                </Banner>

                <ul className="mt-1 text-xs text-ink-soft">
                  {earlier.map((row) => (
                    <li key={row.key} className="tabular" dir="ltr">
                      {row.detail}{' '}
                      <span dir="rtl">({row.qty > 0 ? `زيادة +${row.qty}` : `نقص ${row.qty}`})</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/*
              ⚠ **وضعان صريحان — والمادّةُ الواحدةُ تكون في أكثر من مكان.**
                من وجد خمسًا في الدرج والدفترُ فيه أربعٌ من الرفّ، وكتب «٥»
                في وضع «المجموع»، يُسجَّل ‎+١ لا ‎+٥ — **ولا يظهر الخطأ**.
            */}
            <div className="mt-3 flex flex-wrap gap-2">
              <ModeButton
                active={mode === 'total'}
                label="المعدودُ كلُّه"
                hint="ما كتبتَه هو مجموعُ الصنف في المستودع"
                onClick={() => { setMode('total'); setQty(String(found.onHand)) }}
                disabled={busy}
              />

              <ModeButton
                active={mode === 'add'}
                label="أضِف ما وجدتَه هنا"
                hint="ما كتبتَه يُضاف إلى الدفتر — لمكانٍ ثانٍ للصنف نفسِه"
                onClick={() => { setMode('add'); setQty('') }}
                disabled={busy}
              />
            </div>

            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              <Field label="الرصيدُ في الدفتر">
                <p
                  className="tabular min-h-12 rounded-md border border-line px-3 py-3 text-lg"
                  dir="ltr"
                >
                  {found.onHandKnown ? found.onHand : '؟'}
                </p>
              </Field>

              <Field
                label={mode === 'add' ? 'الكمّيّةُ في هذا المكان' : 'المعدودُ في الرفّ'}
                hint={mode === 'add' ? 'تُضاف إلى الدفتر' : 'المجموعُ كلُّه'}
              >
                <NumberInput value={qty} onChange={setQty} disabled={busy} />
              </Field>

              <Field label="الفرق">
                <p
                  className={
                    'tabular min-h-12 rounded-md border px-3 py-3 text-lg '
                    + (difference === 0
                      ? 'border-line text-ink-soft'
                      : difference > 0 ? 'border-ok text-ok' : 'border-warn text-warn')
                  }
                  dir="ltr"
                >
                  {difference > 0 ? `+${difference}` : difference}
                </p>
              </Field>
            </div>

            {/* ⚠ **وما سيصير عليه الدفترُ يُقال قبل الضغط لا بعده.** */}
            <p className="mt-2 text-sm">
              {difference === 0
                ? <span className="text-ink-soft">المعدودُ يطابق الدفترَ — لا مستندَ يُكتب.</span>
                : (
                  <span>
                    الرصيدُ بعد التسجيل:{' '}
                    <b className="tabular text-base" dir="ltr">{after}</b>
                    <span className="text-ink-soft">
                      {' '}({found.onHand} {difference > 0 ? '+' : '−'} {Math.abs(difference)})
                    </span>
                  </span>
                )}
            </p>

            <p className="mt-1 text-xs text-ink-soft">
              {difference === 0
                ? ''
                : difference > 0
                  ? 'يُكتب مستندُ «زيادةُ جرد» بالفرق — ويبقى في الدفاتر باسمك.'
                  : 'يُكتب مستندُ «نقصُ جرد» بالفرق — ويبقى في الدفاتر باسمك.'}
            </p>

            <div className="mt-3 flex flex-wrap gap-2">
              <Button tone="primary" disabled={!readyCount} onClick={() => void adjust()}>
                {busy ? 'يُسجَّل…' : 'سجّل فرقَ الجرد'}
              </Button>

              {!mayAdjust && difference !== 0 && (
                <span className="text-xs text-warn">
                  تسجيلُ الفرق يحتاج صلاحيّةَ ترحيلِ التسويات.
                </span>
              )}

              <Button onClick={resetRow}>الصنفُ التالي</Button>

              <Link className="text-xs text-brand hover:underline" to={`/products/${found.productId}`}>
                افتح بطاقةَ الصنف
              </Link>
            </div>
          </div>
        )}

        {/* ②-ب باركودٌ مجهول: صنفٌ جديد. */}
        {looked && !found && (
          <div className="mt-4 rounded-md border border-line bg-surface p-3">
            <Banner tone="info">
              لا صنفَ بهذا الباركود — <b>يُعرَّف صنفًا جديدًا</b>، والباركودُ يُحفظ معه.
            </Banner>

            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="القسم" hint="يبقى بعد الحفظ — تجرد رفًّا كاملًا بلا إعادة اختيار">
                <Select value={categoryId} onChange={setCategoryId} disabled={busy}>
                  <option value="">اختر القسم…</option>
                  {(categories.data ?? []).filter((c) => c.isActive).map((category) => (
                    <option key={category.id} value={category.id}>{category.nameAr}</option>
                  ))}
                </Select>
              </Field>

              <Field label="اسمُ الصنف">
                <TextInput
                  value={nameAr}
                  onChange={setNameAr}
                  placeholder="كفر آيفون ١٦ شفّاف"
                  disabled={busy}
                  autoFocus
                />
              </Field>

              <Field label="الكمّيّة">
                <NumberInput value={qty} onChange={setQty} placeholder="10" disabled={busy} />
              </Field>

{!receiving && (
              <Field label="الكلفة" hint="بلا كلفةٍ لا يدخل حسابَ الربح ولا أرضيّةَ سعرٍ له">
                <NumberInput value={cost} onChange={setCost} placeholder="3.500" disabled={busy} />
              </Field>
              )}

              <Field label="سعرُ البيع">
                <NumberInput value={price} onChange={setPrice} placeholder="7.000" disabled={busy} />
              </Field>
            </div>

            <div className="mt-3">
              <ImageField
                path={null}
                busy={busy}
                onPick={setImage}
                hint={image ? `مختارة: ${image.name}` : undefined}
              />
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              <Button tone="primary" disabled={!readyNew} onClick={() => void create()}>
                {busy ? 'يُحفظ…' : 'احفظ الصنفَ الجديد'}
              </Button>

              <Button onClick={resetRow}>تخطَّ</Button>
            </div>
          </div>
        )}

        {error != null && (
          <div className="mt-3">
            <ErrorBanner error={error} />
            <p className="mt-1 text-xs text-ink-soft">
              وما كتبتَه باقٍ كما هو — صحّح الحقلَ وأعِد الحفظ.
            </p>
          </div>
        )}
      </Panel>

      <Panel title={`أُدخل في هذه الجلسة · ${done.length}`}>
        {done.length === 0 ? (
          <Empty>لا شيءَ بعد — امسح أوّلَ باركود.</Empty>
        ) : (
          <>
            <div className="mb-3 flex flex-wrap gap-4 text-sm">
              <span>الأسطر: <b className="tabular">{done.length}</b></span>
              <span>
                صافي القطع: <b className="tabular" dir="ltr">{net > 0 ? `+${net}` : net}</b>
              </span>
            </div>

            <ul className="space-y-1">
              {done.map((row) => (
                <li
                  key={row.key}
                  className="flex flex-wrap items-baseline justify-between gap-2 rounded-md border border-line px-3 py-2 text-sm"
                >
                  <Link className="font-medium hover:underline" to={`/products/${row.id}`}>
                    {row.nameAr}
                  </Link>
                  <span className="text-xs text-ink-soft">
                    {row.detail} ·{' '}
                    <span className="tabular" dir="ltr">
                      {row.qty > 0 ? `+${row.qty}` : row.qty}
                    </span>
                  </span>
                </li>
              ))}
            </ul>

            <p className="mt-3 text-xs text-ink-soft">
              ولا شيءَ منها منشورٌ على الموقع — النشرُ من شاشة الأصناف.
            </p>
          </>
        )}
      </Panel>
    </div>
  )
}

/** ‏`TextInput` لا يُمرّر `ref` — فيُطلب الحقلُ باسمه. */
/**
 * زرُّ وضعٍ — <b>والاختيارُ ظاهرٌ لا مخبوءٌ في قائمة</b>.
 * <p>
 * ومن يعدّ رفًّا بيدٍ وماسحٍ بالأخرى لا يفتح قوائم: الوضعان أمامه، والمُفعَّلُ
 * منهما يُقرأ من بعيد.
 * </p>
 */
function ModeButton({
  active,
  label,
  hint,
  onClick,
  disabled,
}: {
  active: boolean
  label: string
  hint: string
  onClick: () => void
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      title={hint}
      className={
        'min-h-12 rounded-md border px-3 py-2 text-right text-sm disabled:opacity-50 '
        + (active
          ? 'border-brand bg-brand/10 font-bold text-brand'
          : 'border-line text-ink-soft hover:border-ink-soft')
      }
    >
      <span className="block">{label}</span>
      <span className="block text-xs font-normal text-ink-soft">{hint}</span>
    </button>
  )
}

function focusBarcode() {
  const field = document.querySelector<HTMLInputElement>('input[name="stocktake-barcode"]')

  field?.focus()
}

/** الكمّيّاتُ أربعُ خاناتٍ في القاعدة — والتقريبُ مرّةً عند الحدّ. */
function round(value: number) {
  return Math.round(value * 10000) / 10000
}

type Match = {
  productId: string
  variantId: string
  nameAr: string
  categoryName: string | null
  onHand: number

  /** هل قُرئ الرصيدُ فعلًا؟ صفرٌ مجهولٌ وصفرٌ معلومٌ ليسا سواء. */
  onHandKnown: boolean
}

/**
 * ⚠ <b>‏404 وحدَها «صنفٌ جديد»</b> — وما عداها إخفاقٌ يُقال.
 * <p>
 * وانقطاعُ الشبكة يرمي <c>TypeError</c> لا <c>ApiError</c>، فلا يُحسب «غيرَ
 * موجود» — <b>وإلّا دعا انقطاعُ ثانيةٍ إلى إنشاء صنفٍ مكرَّر</b>.
 * </p>
 */
function isNotFound(failure: unknown): boolean {
  return failure instanceof ApiError && failure.status === 404
}

type Row = {
  key: string
  id: string
  nameAr: string
  detail: string
  qty: number

  /**
   * ⚠ <b>ما يجعل المسحةَ الثانيةَ تعرف الأولى.</b> من يجرد رفًّا يمسح الصنفَ
   * نفسَه مرّتين بلا قصد، فبلا هذين الحقلين يبدأ من الصفر ولا يدري أنّه عدَّه.
   */
  variantId?: string

  /** المعدودُ الذي كتبه — لا الفرقُ المُرحَّل. */
  countedQty?: number
}
